import { listGrns, listMaterialStores, type Grn, type GrnStatus, type MaterialStore } from '@api/warehouse-ops';
import { listWarehousesForPicker, type WarehousePickItem } from '@api/work-order';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Group } from '@mantine/core';
import { Plus } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useSearchParams } from 'react-router';

import FgReceiveModal from './FgReceiveModal';
import CreateGrnTypeModal from './CreateGrnTypeModal';
import GrnDetailPanel from './GrnDetailPanel';
import GrnStatusBadge from './GrnStatusBadge';
import StoreReceiveModal from './StoreReceiveModal';

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<GrnStatus, string> = {
    RECEIVED: 'Created', QC_DONE: 'QC Complete', PUTAWAY_DONE: 'Putaway Complete',
};

const InboundContainer: FC = () => {
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<GrnStatus | ''>('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedGrnId, setSelectedGrnId] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerPrefill, setDrawerPrefill] = useState<{ sourceType: 'STN'; sourceId: string } | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [typeOpen, setTypeOpen] = useState(false);
    const [receivingStore, setReceivingStore] = useState<MaterialStore | null>(null);
    const queryClient = useQueryClient();

    // The brand's own warehouses, where raw and packing material is received with its gate QC.
    // Empty only with no own warehouse at all, and then Add GRN is finished goods alone.
    const { data: stores = [] } = useQuery(['material-stores'], listMaterialStores, { staleTime: 5 * 60 * 1000 });

    // Auto-open Create GRN drawer when navigated from Outbound "Create GRN" button
    useEffect(() => {
        const createGrn = searchParams.get('createGrn');
        const stnId = searchParams.get('stnId');
        if (createGrn === 'STN' && stnId) {
            setDrawerPrefill({ sourceType: 'STN', sourceId: stnId });
            setDrawerOpen(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    /*
     * Receive on a transfer bound for a material store lands here as ?store=<id>&stn=<code>: open
     * that store's RM/PM GRN. Only `store` is taken off the link; the receive grid reads `stn`,
     * picks the transfer once its list has it, and drops it itself.
     */
    const linkedStore = searchParams.get('store');
    useEffect(() => {
        if (!linkedStore) return;
        const store = stores.find((candidate) => candidate.warehouseId === linkedStore);
        if (!store) return;
        setReceivingStore(store);
        setSearchParams((params) => {
            params.delete('store');
            return params;
        }, { replace: true });
    }, [linkedStore, stores, setSearchParams]);

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['warehouses-picker-internal'],
        queryFn: () => listWarehousesForPicker(),
        staleTime: Infinity,
    });
    const warehouseNameMap = useMemo(() =>
        new Map((warehousesQuery.data ?? []).map((w) => [w.warehouseId, w.name])),
        [warehousesQuery.data],
    );

    const query = useQuery({
        queryKey: ['grns', page, statusFilter, dateFrom, dateTo],
        queryFn: () => listGrns({
            page, take: PAGE_SIZE,
            status: statusFilter || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
        }),
    });

    const data = query.data?.data ?? [];
    const meta = query.data?.meta;

    const columns: DataSheetColumn<Grn>[] = [
        {
            key: 'code', header: 'GRN ID', width: 150, sticky: true,
            render: (_, r) => <span className="font-medium">{r.code}</span>,
        },
        {
            key: 'itemType', header: 'Type', width: 90,
            render: (_, r) => (r.itemType === 'material' ? 'RM/PM' : 'FG'),
        },
        {
            key: 'sourceWarehouseId', header: 'Warehouse', width: 200,
            render: (_, r) => warehouseNameMap.get(r.sourceWarehouseId) ?? r.sourceWarehouseId,
        },
        { key: 'itemCount', header: 'Items', width: 80, align: 'right', render: (_, r) => r.itemCount ?? '—' },
        {
            key: 'sourceId', header: 'Source PO / STN', width: 220,
            render: (_, r) => r.sourceId ? `${r.sourceType.replace('_', ' ')}: ${r.sourceLabel ?? r.sourceId}` : '—',
        },
        {
            key: 'status', header: 'Status', width: 160,
            render: (_, r) => (
                <GrnStatusBadge status={r.status} reversed={r.reversed} itemType={r.itemType} label={STATUS_LABEL[r.status]} />
            ),
        },
        { key: 'receivedDate', header: 'Date', width: 120, render: (_, r) => r.receivedDate },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="📥"
                    title="Inbound"
                    subtitle="Manage goods receipts, QC checks, and putaway"
                />
                <Button
                    leftSection={<Plus size={14} />}
                    onClick={() => (stores.length > 0 ? setTypeOpen(true) : setDrawerOpen(true))}
                >
                    Add GRN
                </Button>
            </Group>

            <DataSheet<Grn>
                className="flex-1 min-h-0"
                data={data}
                columns={columns}
                isLoading={query.isLoading}
                getRowKey={(r) => r.id}
                onRowClick={(r) => setSelectedGrnId(r.id)}
                toolbarControls={
                    <Group gap="xs">
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value as GrnStatus | ''); setPage(1); }}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                        >
                            <option value="">All statuses</option>
                            <option value="RECEIVED">Created</option>
                            <option value="QC_DONE">QC Complete</option>
                            <option value="PUTAWAY_DONE">Putaway Complete</option>
                        </select>
                        <input type="date" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                        />
                        <input type="date" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                        />
                    </Group>
                }
                footer={
                    meta && meta.pageCount > 1 ? (
                        <Group justify="center" p="sm">
                            <Button size="xs" variant="subtle" disabled={!meta.hasPreviousPage} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                            <span style={{ fontSize: 12 }}>Page {meta.page} of {meta.pageCount}</span>
                            <Button size="xs" variant="subtle" disabled={!meta.hasNextPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
                        </Group>
                    ) : undefined
                }
            />

            <CreateGrnTypeModal
                opened={typeOpen}
                stores={stores}
                onClose={() => setTypeOpen(false)}
                onContinue={(pick) => {
                    setTypeOpen(false);
                    if (pick.kind === 'fg') setDrawerOpen(true);
                    else setReceivingStore(stores.find((store) => store.warehouseId === pick.warehouseId) ?? null);
                }}
            />
            <StoreReceiveModal
                store={receivingStore}
                onClose={() => setReceivingStore(null)}
                onSaved={() => {
                    setReceivingStore(null);
                    void queryClient.invalidateQueries(['grns']);
                }}
            />
            <FgReceiveModal
                opened={drawerOpen}
                prefill={drawerPrefill ?? undefined}
                onClose={() => { setDrawerOpen(false); setDrawerPrefill(null); }}
            />
            {selectedGrnId && (
                <GrnDetailPanel grnId={selectedGrnId} onClose={() => setSelectedGrnId(null)} />
            )}
        </div>
    );
};

export default InboundContainer;
